import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Tooltip,
    Select,
    MenuItem,
} from "@mui/material";
import { sectionBox } from "../styles/ComponentStyles";
import { useTranslate } from "../components/Translate/TranslateContext";
import { useRegional } from "../components/Regional/RegionalContext";

export const weatherIcons = {
    0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️", 45: "🌫️", 48: "🌫️", 51: "🌦️",
    53: "🌧️", 55: "🌧️", 56: "🥶", 57: "🥶", 61: "🌧️", 63: "🌧️", 65: "🌧️",
    66: "🥶", 67: "🌨️", 71: "❄️", 73: "❄️", 75: "❄️", 77: "🌨️", 80: "🌦️",
    81: "🌧️", 82: "⛈️", 85: "🌨️", 86: "🌨️", 95: "🌩️", 96: "⛈️", 99: "⛈️",
};

export default function Banner({ summaries, mode = "next" }) {
    const { translate, language } = useTranslate();
    const { units } = useRegional();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [page, setPage] = useState(0);
    const [paused, setPaused] = useState(false);

    const [pastForecast, setPastForecast] = useState([]);

    const NAMES_PER_PAGE = 3;
    const safeSummaries = summaries || [];
    const showDropdown = safeSummaries.length > 10;

    const currentSummary = safeSummaries[currentIndex];

    useEffect(() => {
        if (mode !== "past" || !currentSummary?.latitude || !currentSummary?.longitude) return;

        async function fetchPastWeather() {
            try {
                const endDate = new Date();
                const startDate = new Date();
                startDate.setDate(endDate.getDate() - 6);

                const startStr = startDate.toISOString().slice(0, 10);
                const endStr = endDate.toISOString().slice(0, 10);

                const res = await fetch(
                    `https://api.open-meteo.com/v1/forecast?latitude=${currentSummary.latitude}&longitude=${currentSummary.longitude}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max&timezone=auto&start_date=${startStr}&end_date=${endStr}`
                );
                const data = await res.json();

                if (data?.daily?.time) {
                    const formatted = data.daily.time.map((dateStr, idx) => ({
                        date: dateStr,
                        weather_code_wmo: data.daily.weather_code[idx],
                        temp_max: data.daily.temperature_2m_max[idx],
                        temp_min: data.daily.temperature_2m_min[idx],
                        precipitation_mm: data.daily.precipitation_sum[idx],
                        windspeed_max: data.daily.wind_speed_10m_max[idx],
                    }));
                    setPastForecast(formatted);
                }
            } catch (err) {
                console.error("Failed to fetch past weather:", err);
            }
        }

        fetchPastWeather();
    }, [mode, currentSummary?.latitude, currentSummary?.longitude]);

    const totalPages = Math.ceil(safeSummaries.length / NAMES_PER_PAGE);
    const pagedSummaries = safeSummaries.slice(
        page * NAMES_PER_PAGE,
        (page + 1) * NAMES_PER_PAGE
    );

    const handleGardenSelect = (idx) => {
        setCurrentIndex(idx);
        setPage(Math.floor(idx / NAMES_PER_PAGE));
    };

    const formatShortDate = (date) => {
        return date.toLocaleDateString(language || undefined, {
            month: "2-digit",
            day: "2-digit",
        });
    };

    const formatTemp = (value) => {
        if (value === null || value === undefined) return "—";

        const numericValue = Number(value);
        const converted =
            units.temperature === "fahrenheit"
                ? numericValue * 1.8 + 32
                : numericValue;

        return `${Math.round(converted)}°`;
    };

    const formatRain = (value) => {
        if (value === null || value === undefined) {
            return units.rainfall === "in" ? "0in" : "0mm";
        }

        const numericValue = Number(value);

        if (units.rainfall === "in") {
            const inches = numericValue / 25.4;
            return `${Number(inches.toFixed(inches < 1 ? 2 : 1))}in`;
        }

        return `${Math.round(numericValue)}mm`;
    };

    const formatWind = (valueMetersPerSecond) => {
        if (valueMetersPerSecond === null || valueMetersPerSecond === undefined) {
            return "—";
        }

        const numericValue = Number(valueMetersPerSecond);

        if (units.wind === "mph") {
            return `${Math.round(numericValue * 2.23694)}mph`;
        }

        return `${Math.round(numericValue * 3.6)}km/h`;
    };

    const targetDays = useMemo(() => {
        return Array.from({ length: 7 }, (_, index) => {
            const day = new Date();
            
            if (mode === "past") {
                day.setDate(day.getDate() - (6 - index));
            } else {
                day.setDate(day.getDate() + index);
            }

            const year = day.getFullYear();
            const month = String(day.getMonth() + 1).padStart(2, "0");
            const dateStr = String(day.getDate()).padStart(2, "0");

            return {
                iso: `${year}-${month}-${dateStr}`,
                display: formatShortDate(day),
            };
        });
    }, [language, mode]);

    const extractIsoFromPayload = (dateStr) => {
        if (!dateStr) return "";

        const str = String(dateStr);

        if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
            return str.substring(0, 10);
        }

        const parts = str.split(" ");
        const monthMap = {
            Jan: "01",
            Feb: "02",
            Mar: "03",
            Apr: "04",
            May: "05",
            Jun: "06",
            Jul: "07",
            Aug: "08",
            Sep: "09",
            Oct: "10",
            Nov: "11",
            Dec: "12",
        };

        if (parts.length > 3 && monthMap[parts[1]]) {
            return `${parts[3]}-${monthMap[parts[1]]}-${parts[2].padStart(2, "0")}`;
        }

        return "";
    };

    const activeForecast = mode === "past" ? pastForecast : (currentSummary?.forecast ?? []);

    return (
        <Box
            sx={{
                ...sectionBox,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 225px" },
                gap: 2,
                padding: { xs: 1, md: "1rem 5%" },
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            <Box
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gap: 1,
                    width: "100%",
                }}
            >
                {targetDays.map((targetDay, idx) => {
                        const dayForecast = activeForecast.find(
                            (forecast) => 
                                extractIsoFromPayload(forecast.date) === targetDay.iso
                        );

                    return (
                        <Paper
                            key={idx}
                            sx={{
                                p: 0.5,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "space-between",
                                bgcolor: "var(--color-bg-primary)",
                                color: "var(--color-text-primary)",
                                borderRadius: 1,
                                height: 124,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                {targetDay.display}
                            </Typography>

                            <Tooltip
                                title={translate(
                                    `weather_${dayForecast?.weather_code_wmo ?? 0}`
                                )}
                            >
                                <Typography sx={{ fontSize: 20 }}>
                                    {weatherIcons[dayForecast?.weather_code_wmo] ?? "—"}
                                </Typography>
                            </Tooltip>

                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                            >
                                {formatTemp(dayForecast?.temp_max)} /{" "}
                                {formatTemp(dayForecast?.temp_min)}
                            </Typography>

                            <Typography variant="caption">
                                💧 {dayForecast?.precipitation_probability_max ?? 0}%{" "}
                                {formatRain(dayForecast?.precipitation_mm)}
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: "0.65rem",
                                    opacity: 0.85,
                                }}
                            >
                                🌬 {formatWind(dayForecast?.windspeed_max)}
                            </Typography>
                        </Paper>
                    );
                })}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    width: "100%",
                    justifyContent: "center",
                }}
            >
                {showDropdown ? (
                    <Select
                        size="small"
                        value={currentIndex}
                        onChange={(e) => handleGardenSelect(Number(e.target.value))}
                        sx={{
                            width: "100%",
                            height: 36,
                            bgcolor: "var(--color-green-strong)",
                            color: "var(--color-text-primary)",
                        }}
                    >
                        {safeSummaries.map((summary, idx) => (
                            <MenuItem
                                key={summary.id || summary.garden_id}
                                value={idx}
                            >
                                {summary.garden_name}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            {pagedSummaries.map((summary, idx) => {
                                const absoluteIdx = idx + page * NAMES_PER_PAGE;
                                const selected = absoluteIdx === currentIndex;

                                return (
                                    <Paper
                                        key={summary.id || summary.garden_id}
                                        onClick={() => handleGardenSelect(absoluteIdx)}
                                        sx={{
                                            height: 36,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            bgcolor: selected
                                                ? "var(--color-green-strong)"
                                                : "var(--color-bg-primary)",
                                            color: selected
                                                ? "var(--color-text-inverse)"
                                                : "var(--color-text-primary)",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: selected ? 700 : 500,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                px: 1,
                                            }}
                                        >
                                            {summary.garden_name}
                                        </Typography>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {totalPages > 1 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    justifyContent: "center",
                                }}
                            >
                                {Array.from({ length: totalPages }).map((_, idx) => (
                                    <Box
                                        key={idx}
                                        onClick={() => setPage(idx)}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            bgcolor:
                                                idx === page
                                                    ? "var(--color-green-strong)"
                                                    : "var(--color-bg-primary)",
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}